import { httpDelete, httpGet, httpPatch, httpPost } from "@/api/connection";

import type {
  AssetValidation,
  CreateAssetValidationBody,
  DeleteAssetValidationResponse,
  GetAssetValidationsQuery,
  QueryAssetValidationsResult,
  UpdateAssetValidationBody,
} from "./asset-validations.types";

// Shared trap for every wrapper here: connection.js resolves HTTP failures as
// { success: false, error } instead of rejecting (and toasts unless
// skipErrorToast), so callers must runtime-check the payload shape — see
// isConnectionFailure / connectionFailureMessage in connection.types.ts.

// GET /validations/:uuid returns a bare row; 404 resolves as
// { success: false }.
function getAssetValidations(
  query: GetAssetValidationsQuery & { uuid: string }
): Promise<AssetValidation>;
// GET /validations. Strict query schema (see QueryAssetValidationsParams);
// there is no POST /validations/query, so `filter` is sent as a query param.
function getAssetValidations(
  query?: GetAssetValidationsQuery
): Promise<QueryAssetValidationsResult>;
async function getAssetValidations(query: GetAssetValidationsQuery = {}) {
  const { uuid, skipErrorToast, ...params } = query;

  if (uuid) {
    return httpGet(`/validations/${uuid}`, {}, { skipErrorToast });
  }

  return httpGet(`/validations`, params, { skipErrorToast });
}

const getAssetValidationById = (
  uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<AssetValidation> => getAssetValidations({ uuid, ...options });

// POST /validations responds 201 with the created row.
async function createAssetValidation(
  values: CreateAssetValidationBody,
  options: { skipErrorToast?: boolean } = {}
): Promise<AssetValidation> {
  return await httpPost(`/validations`, values, options);
}

// PATCH /validations/:uuid merges only the keys present and, unlike
// PATCH /assets/:uuid, responds with the updated row — no refetch needed.
async function updateAssetValidation(
  uuid: string,
  values: UpdateAssetValidationBody,
  options: { skipErrorToast?: boolean } = {}
): Promise<AssetValidation> {
  return await httpPatch(`/validations/${uuid}`, values, options);
}

// DELETE /validations/:uuid. Report attachments survive as plain asset
// attachments (validation_uuid nulled); their files are not deleted.
async function deleteAssetValidation(
  uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<DeleteAssetValidationResponse> {
  return await httpDelete(`/validations/${uuid}`, undefined, options);
}

export const AssetValidationsAPI = {
  get: getAssetValidations,
  getById: getAssetValidationById,
  create: createAssetValidation,
  update: updateAssetValidation,
  delete: deleteAssetValidation,
};
